import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import * as changeCase from 'change-case';
import PaymentForm from './PaymentForm';
import PolicyStepsHeader from '../../common/PolicyStepsHeader';
var paymentValidationSchema = Yup.object({
    customer: Yup.object({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        address1: Yup.string().required(),
        address2: Yup.string(),
        city: Yup.string().required(),
        state: Yup.string().required(),
        email: Yup.string().email().required()
    }),
    venue: Yup.object({
        honoree1FirstName: Yup.string().required(),
        honoree1LastName: Yup.string().required(),
        honoree2FirstName: Yup.string(),
        honoree2LastName: Yup.string(),
        venueName: Yup.string().required()
    }),
    agree: Yup.boolean().is([true]).required()
});
var Payment = function (_a) {
    var latestQuote = _a.latestQuote, onPurchaseQuote = _a.onPurchaseQuote;
    var formik = useFormik({
        initialValues: {},
        onSubmit: onPurchaseQuote,
        validationSchema: paymentValidationSchema
    });
    var errors = formik.errors, setFieldValue = formik.setFieldValue;
    var printCurrency = function (amount) {
        return "$".concat(amount.toLocaleString('en-us', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }));
    };
    var _b = useMemo(function () {
        var _a, _b;
        var coverages = Object.entries(((_b = (_a = latestQuote === null || latestQuote === void 0 ? void 0 : latestQuote.items) === null || _a === void 0 ? void 0 : _a.requiredParams) === null || _b === void 0 ? void 0 : _b.coverages) || {})
            .map(function (_a) {
            var key = _a[0], value = _a[1];
            if (value === null || value === void 0 ? void 0 : value.enabled)
                return {
                    label: changeCase.capitalCase(key),
                    premium: Number(value.premium)
                };
        })
            .filter(function (c) { return c !== undefined; });
        var total = coverages.reduce(function (acc, curr) { return acc + curr.premium; }, 0);
        return {
            coverages: coverages,
            total: total
        };
    }, [latestQuote]), coverages = _b.coverages, total = _b.total;
    return (React.createElement("div", { className: "w-full md:w-[calc(100vw-104px)] h-full md:absolute md:top-0 flex flex-col items-center justify-start overflow-hidden" },
        React.createElement("div", { className: "w-full" },
            React.createElement(PolicyStepsHeader, { theme: "modern", steps: [
                    {
                        stepText: 'Event Details',
                        isStepActive: false,
                        isStepCompleted: true
                    },
                    {
                        stepText: 'Coverages',
                        isStepActive: false,
                        isStepCompleted: true
                    },
                    {
                        stepText: 'Payment',
                        isStepActive: true,
                        isStepCompleted: false
                    },
                ], hideNextButton: true })),
        React.createElement("div", { className: "w-full flex-1 md:h-[calc(100vh-140px)] overflow-y-auto" },
            React.createElement("form", { className: "flex flex-col lg:flex-row lg:h-full w-full max-w-[1280px] mx-auto", onSubmit: function (e) {
                    e.preventDefault();
                    formik.handleSubmit();
                } },
                React.createElement("div", { className: "flex-1 pt-6 lg:pt-8 pb-6 lg:pr-10 overflow-y-auto px-4 md:px-0" },
                    React.createElement("h1", { className: "text-2xl md:text-[32px] font-bold text-[#15161E] mb-6 md:mb-8 text-left " }, "Payment"),
                    React.createElement(PaymentForm, { formik: formik })),
                React.createElement("div", { className: "flex flex-col h-auto lg:h-full w-full lg:w-[360px] border-t md:border-t-0 md:border-l border-[#E4E4E4] pt-6 md:pt-8 pb-6 px-4" },
                    React.createElement("h2", { className: "text-2xl md:text-[32px] font-bold text-[#15161E] mb-6 md:mb-8" }, "Your Insurance"),
                    React.createElement("ul", { className: "flex flex-col gap-6 text-[#15161E]" },
                        coverages.map(function (c) { return (React.createElement("li", { key: c.label, className: "flex items-center justify-between" },
                            React.createElement("h3", { className: "font-semibold" }, c.label),
                            React.createElement("h4", { className: "font-semibold" }, printCurrency(c.premium)))); }),
                        React.createElement("li", { className: "flex items-center justify-between" },
                            React.createElement("h3", { className: "font-bold text-lg md:text-xl" }, "Total"),
                            React.createElement("h4", { className: "font-bold text-lg md:text-xl" }, printCurrency(total)))),
                    React.createElement("div", { className: "hidden md:flex flex-1" }),
                    React.createElement("div", null,
                        React.createElement("div", { className: "flex items-center gap-4" },
                            React.createElement("div", { className: "relative w-12" },
                                React.createElement("input", { type: "checkbox", id: "agree", name: "agree", className: "hidden peer", onChange: function (e) {
                                        setFieldValue('agree', e.target.checked, true);
                                    } }),
                                React.createElement("label", { htmlFor: "agree", className: "relative z-1 block w-full h-6 border-2 border-[#808892] peer-checked:border-primary rounded-full cursor-pointer transition-all" }),
                                React.createElement("label", { htmlFor: "agree", className: "absolute top-1 left-1 size-4 bg-[#808892] rounded-full peer-checked:translate-x-6 cursor-pointer transition-all peer-checked:bg-primary" })),
                            React.createElement("p", { className: "flex-1 text-xs md:text-sm text-[#15161E] text-left" }, "I have read and understood the fraud warning, fully earned premium, selected exclusions and privacy disclosures and wish to continue.")),
                        Boolean(errors === null || errors === void 0 ? void 0 : errors.agree) && (React.createElement("p", { className: " text-red-500 text-sm text-left" }, "You must agree to the terms")),
                        React.createElement("button", { className: "w-full h-12 bg-primary text-white font-bold text-sm rounded-lg mt-4" }, "Confirm & Pay")))))));
};
export default Payment;
